import { API_BASE } from './api-base';

const API = API_BASE;

// 04.02.2026: Automatischer Refresh-Flow.
// Wenn ein API-Call 401 bekommt, versuchen wir EINMAL den Refresh-Endpoint
// zu treffen — der liest den httpOnly-Cookie `refresh_token` (7 Tage
// gültig) und liefert einen neuen access_token zurück. Bei Erfolg wird
// der Original-Request wiederholt, der Nutzer merkt nichts. Nur wenn der
// Refresh selbst fehlschlägt (Refresh-Token abgelaufen / ungültig / weg),
// wird auf /login umgeleitet.
//
// Concurrency-Schutz: laufen viele Requests parallel und bekommen alle
// 401, darf nur EIN Refresh-Call rausgehen. Alle anderen warten auf das
// selbe Promise und retryen dann mit dem frischen Token.
let _refreshInFlight = null;

function _redirectToLogin() {
  try {
    localStorage.removeItem('ees_token');
  } catch (_) {}
  // Kein harter Reload wenn wir bereits auf der Login-Seite sind — sonst
  // Endlosschleife bei kaputtem Server.
  if (!/\/login(\?|$|\/)/.test(window.location.pathname)) {
    window.location.href = '/dashboard/login';
  }
}

async function _refreshOnce() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return null;
      const data = await r.json().catch(() => ({}));
      if (data?.token) {
        try { localStorage.setItem('ees_token', data.token); } catch (_) {}
        return data.token;
      }
      return null;
    } catch (_) {
      return null;
    } finally {
      // Nach ~1s den In-Flight-Cache freigeben, damit späterer Fehler-
      // Zyklus wieder refreshen darf.
      setTimeout(() => { _refreshInFlight = null; }, 1000);
    }
  })();
  return _refreshInFlight;
}

function _buildHeaders(options) {
  const token = (() => {
    try { return localStorage.getItem('ees_token'); } catch (_) { return null; }
  })();
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  return headers;
}

export async function authFetch(path, options = {}) {
  // Auth-Refresh selbst darf NICHT rekursiv über authFetch laufen —
  // wenn jemand von außen /api/auth/refresh oder /login ruft, direkt
  // durchreichen ohne Retry-Logik.
  const isAuthEndpoint = /^\/api\/auth\/(refresh|login|logout)(\?|$)/.test(path);

  const doFetch = async () => fetch(`${API}${path}`, {
    ...options,
    headers: _buildHeaders(options),
    credentials: 'include',
  });

  let res = await doFetch();

  if (res.status === 401 && !isAuthEndpoint) {
    // Versuche EINMAL zu refreshen und den Original-Request zu wiederholen.
    const newToken = await _refreshOnce();
    if (newToken) {
      res = await doFetch();
      if (res.status !== 401) return res;
    }
    // Refresh selbst hat auch 401 geliefert → wirklich raus.
    _redirectToLogin();
    throw new Error('Nicht authentifiziert');
  }

  return res;
}
